import {
  type EpisodeGenerateQueueState,
  summarizeEpisodeQueue,
} from './comicEpisodeGenerateQueue'
import {
  episodeQueueIslandPhaseDotClass,
  episodeQueueIslandPhaseRailSemantic,
  episodeQueueProgressFillClass,
  episodeQueueStatusChipClass,
} from './episodeQueueTimelineTheme'

/** 漫画话级队列在灵动岛上的活动态（业务语义，非主题壳层） */
export type ComicEpisodeQueueIslandPhase =
  | 'queued'
  | 'running'
  | 'awaiting'
  | 'success'
  | 'warning'
  | 'cancelled'

export function comicEpisodeQueueIslandPhaseLabel(phase: ComicEpisodeQueueIslandPhase): string {
  switch (phase) {
    case 'queued':
    case 'running':
    case 'awaiting':
      return '生成中'
    case 'success':
    case 'warning':
    case 'cancelled':
      return '已完成'
  }
}

export function comicEpisodeQueueIslandPhaseDotClass(phase: ComicEpisodeQueueIslandPhase): string {
  return episodeQueueIslandPhaseDotClass(phase)
}

export function comicEpisodeQueueIslandPhaseChipClass(phase: ComicEpisodeQueueIslandPhase): string {
  const semantic = episodeQueueIslandPhaseRailSemantic(phase)
  return [
    'inline-flex min-w-[3rem] shrink-0 items-center justify-center rounded-full border px-1.5 py-px text-[9px] font-bold uppercase tracking-wide',
    episodeQueueStatusChipClass(
      semantic === 'warn'
        ? 'running'
        : semantic === 'danger'
          ? 'failed'
          : semantic === 'success'
            ? 'done'
            : semantic === 'cancelled'
              ? 'cancelled'
              : 'pending',
    ),
  ].join(' ')
}

export function comicEpisodeQueueIslandProgressPercent(queue: EpisodeGenerateQueueState): number {
  const s = summarizeEpisodeQueue(queue)
  if (s.total <= 0) return 0
  const processed = s.succeeded + s.failed + s.awaiting + s.cancelled
  return Math.min(100, Math.round((processed / s.total) * 100))
}

export function comicEpisodeQueueIslandProgressFillClass(
  queue: EpisodeGenerateQueueState,
  phase: ComicEpisodeQueueIslandPhase,
): string {
  const s = summarizeEpisodeQueue(queue)
  return episodeQueueProgressFillClass({
    cancelled: queue.cancelled || phase === 'cancelled',
    finished: s.finished,
    hasFailed: s.failed > 0 || phase === 'warning',
    inProgress: phase === 'running' || phase === 'queued' || phase === 'awaiting',
  })
}

export function deriveComicEpisodeQueueIslandPhase(
  queue: EpisodeGenerateQueueState,
): ComicEpisodeQueueIslandPhase {
  if (queue.cancelled) return 'cancelled'
  const s = summarizeEpisodeQueue(queue)
  if (s.running > 0) return 'running'
  if (s.pending > 0 && s.running === 0) return 'queued'
  if (s.awaiting > 0) return 'awaiting'
  if (s.finished) {
    return s.failed > 0 ? 'warning' : 'success'
  }
  return 'running'
}

export function formatComicEpisodeQueueIslandProgress(queue: EpisodeGenerateQueueState): {
  compactSubtitle: string
  expandedDescription: string
} {
  const phase = deriveComicEpisodeQueueIslandPhase(queue)
  const phaseLabel = comicEpisodeQueueIslandPhaseLabel(phase)
  const s = summarizeEpisodeQueue(queue)
  const currentTitle =
    queue.currentPageId != null ? (queue.pageTitles[queue.currentPageId] ?? null) : null

  if (phase === 'success') {
    return {
      compactSubtitle: `已完成 · ${s.succeeded}/${s.total}`,
      expandedDescription: `${phaseLabel} · 共 ${s.total} 页已全部生成`,
    }
  }
  if (phase === 'warning') {
    const parts = [`已完成`, `${s.succeeded}/${s.total}`]
    if (s.failed > 0) parts.push(`失败 ${s.failed}`)
    if (s.awaiting > 0) parts.push(`待选 ${s.awaiting}`)
    const line = parts.join(' · ')
    return { compactSubtitle: line, expandedDescription: line }
  }
  if (phase === 'cancelled') {
    return {
      compactSubtitle: '已取消',
      expandedDescription: `已取消 · 已完成 ${s.succeeded + s.failed + s.awaiting}/${s.total}`,
    }
  }
  if (phase === 'awaiting') {
    const line = [phaseLabel, currentTitle ?? `待选 ${s.awaiting} 页`].filter(Boolean).join(' · ')
    return { compactSubtitle: line, expandedDescription: line }
  }

  const processed = s.succeeded + s.failed + s.awaiting + s.cancelled
  const progress = `${processed}/${s.total}`
  const compactParts = [phaseLabel, progress]
  if (s.failed > 0) compactParts.push(`失败 ${s.failed}`)
  const expandedParts = [...compactParts]
  if (currentTitle) expandedParts.push(currentTitle)

  return {
    compactSubtitle: compactParts.join(' · '),
    expandedDescription: expandedParts.join(' · '),
  }
}
